import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabase/config';
import { checkInService } from '../../services/checkInService';
import { User, Mail, Phone, CheckCircle2, ArrowLeft, AlertCircle, Lock, X } from 'lucide-react';
import { generateShortId } from '../../utils/customer';
import { recordConsent } from '../../utils/consent';
import Logo from '../../components/common/Logo';
import SuccessView from './checkin/SuccessView';

const countries = [
    { code: '+90', name: 'TR', full: 'Turkey' },
    { code: '+994', name: 'AZ', full: 'Azerbaijan' },
    { code: '+1', name: 'US/CA', full: 'USA/Canada' },
    { code: '+44', name: 'UK', full: 'United Kingdom' },
    { code: '+49', name: 'DE', full: 'Germany' },
    { code: '+33', name: 'FR', full: 'France' },
    { code: '+31', name: 'NL', full: 'Netherlands' },
    { code: '+39', name: 'IT', full: 'Italy' },
    { code: '+34', name: 'ES', full: 'Spain' },
    { code: '+7', name: 'RU/KZ', full: 'Russia/Kazakhstan' },
    { code: '+86', name: 'CN', full: 'China' },
    { code: '+81', name: 'JP', full: 'Japan' },
    { code: '+82', name: 'KR', full: 'South Korea' },
    { code: '+91', name: 'IN', full: 'India' },
    { code: '+971', name: 'AE', full: 'UAE' },
    { code: '+966', name: 'SA', full: 'Saudi Arabia' },
    { code: '+974', name: 'QA', full: 'Qatar' },
    { code: '+965', name: 'KW', full: 'Kuwait' },
    { code: '+30', name: 'GR', full: 'Greece' },
    { code: '+359', name: 'BG', full: 'Bulgaria' },
    { code: '+40', name: 'RO', full: 'Romania' },
    { code: '+380', name: 'UA', full: 'Ukraine' },
    { code: '+48', name: 'PL', full: 'Poland' },
    { code: '+43', name: 'AT', full: 'Austria' },
    { code: '+41', name: 'CH', full: 'Switzerland' },
    { code: '+32', name: 'BE', full: 'Belgium' },
    { code: '+46', name: 'SE', full: 'Sweden' },
    { code: '+47', name: 'NO', full: 'Norway' },
    { code: '+45', name: 'DK', full: 'Denmark' },
    { code: '+358', name: 'FI', full: 'Finland' },
    { code: '+351', name: 'PT', full: 'Portugal' },
    { code: '+353', name: 'IE', full: 'Ireland' },
    { code: '+61', name: 'AU', full: 'Australia' },
    { code: '+64', name: 'NZ', full: 'New Zealand' },
    { code: '+55', name: 'BR', full: 'Brazil' },
    { code: '+52', name: 'MX', full: 'Mexico' },
    { code: '+20', name: 'EG', full: 'Egypt' },
    { code: '+212', name: 'MA', full: 'Morocco' },
    { code: '+27', name: 'ZA', full: 'South Africa' },
    { code: '+234', name: 'NG', full: 'Nigeria' },
    { code: '+254', name: 'KE', full: 'Kenya' },
    { code: '+213', name: 'DZ', full: 'Algeria' },
    { code: '+92', name: 'PK', full: 'Pakistan' },
    { code: '+880', name: 'BD', full: 'Bangladesh' },
    { code: '+62', name: 'ID', full: 'Indonesia' },
    { code: '+60', name: 'MY', full: 'Malaysia' },
    { code: '+66', name: 'TH', full: 'Thailand' },
    { code: '+84', name: 'VN', full: 'Vietnam' },
    { code: '+63', name: 'PH', full: 'Philippines' },
    { code: '+65', name: 'SG', full: 'Singapore' },
    { code: '+93', name: 'AF', full: 'Afghanistan' },
    { code: '+993', name: 'TM', full: 'Turkmenistan' },
    { code: '+998', name: 'UZ', full: 'Uzbekistan' },
    { code: '+992', name: 'TJ', full: 'Tajikistan' },
    { code: '+996', name: 'KG', full: 'Kyrgyzstan' },
    { code: '+374', name: 'AM', full: 'Armenia' },
    { code: '+972', name: 'IL', full: 'Israel' },
    { code: '+962', name: 'JO', full: 'Jordan' },
    { code: '+961', name: 'LB', full: 'Lebanon' },
    { code: '+963', name: 'SY', full: 'Syria' },
    { code: '+964', name: 'IQ', full: 'Iraq' },
    { code: '+98', name: 'IR', full: 'Iran' },
    { code: '+968', name: 'OM', full: 'Oman' },
    { code: '+967', name: 'YE', full: 'Yemen' },
    { code: '+973', name: 'BH', full: 'Bahrain' }
];

const capitalizeWords = (str) => {
    if (!str) return '';
    return str.split(' ').map(word =>
        word.length > 0 ? word.charAt(0).toLocaleUpperCase('tr') + word.slice(1).toLocaleLowerCase('tr') : ''
    ).join(' ');
};

const CustomerLogin = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [checkInMode, setCheckInMode] = useState('register'); // register, login
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [kvkkAccepted, setKvkkAccepted] = useState(false);
    const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '' });
    const [countryCode, setCountryCode] = useState('+90');
    const [loginIdentifier, setLoginIdentifier] = useState('');
    const [loginPassword, setLoginPassword] = useState('');
    const [error, setError] = useState('');
    const [menuData, setMenuData] = useState(null);
    const [result, setResult] = useState(null);
    const [loggedInTenant, setLoggedInTenant] = useState(null);

    // Forgot password state
    const [resetEmail, setResetEmail] = useState('');
    const [forgotPasswordStep, setForgotPasswordStep] = useState(0); // 0: enter email, 1: enter new password
    const [newPassword, setNewPassword] = useState('');

    // OTP state
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [otpCode, setOtpCode] = useState(['', '', '', '']);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [otpResendCooldown, setOtpResendCooldown] = useState(0);
    const otpInputRefs = useRef([React.createRef(), React.createRef(), React.createRef(), React.createRef()]);

    // OTP resend cooldown timer
    useEffect(() => {
        if (otpResendCooldown <= 0) return;
        const timer = setInterval(() => {
            setOtpResendCooldown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [otpResendCooldown]);

    // Auto-focus first OTP input when modal opens
    useEffect(() => {
        if (showOtpModal) {
            setTimeout(() => {
                otpInputRefs.current[0]?.current?.focus();
            }, 100);
        }
    }, [showOtpModal]);

    // Default "ugra" branding for global login
    const tenant = {
        name: 'ugra',
        theme: {
            logoUrl: '/favicon.svg',
            primary: 'var(--primary)'
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            const identifier = loginIdentifier.trim().toLowerCase();
            const isEmail = identifier.includes('@');

            let query;
            if (isEmail) {
                query = supabase.from('customers').select('*').eq('email', identifier);
            } else {
                const cleanPhone = identifier.replace(/[^0-9]/g, '');
                query = supabase.from('customers').select('*').eq('phone', cleanPhone);
            }

            const { data: rows, error: fetchErr } = await query;
            if (fetchErr) throw fetchErr;

            if (rows && rows.length > 0) {
                const firstRow = rows[0];

                // Check Password
                if (firstRow.password && firstRow.password !== loginPassword) {
                    setError(t('login.invalidPassword', 'Girdiğiniz şifre yanlış.'));
                    setStatus('idle');
                    return;
                }
                // User found
                let targetTenantId = firstRow.tenant_id;
                let targetCustomerId = firstRow.id;

                localStorage.setItem('crm_global_customer_id', targetCustomerId);

                const { data: tData } = await supabase.from('tenants').select('*').eq('id', targetTenantId).single();
                // Ensure checkInService.fetchMenu is used here to get properly mapped menu data based on active_menu_id
                const mData = await checkInService.fetchMenu(targetTenantId, tData?.active_menu_id);

                setLoggedInTenant(tData);
                setMenuData(mData);
                setResult({
                    isNew: false,
                    name: firstRow.first_name,
                    points: 0,
                    total: firstRow.points,
                    tenantId: targetTenantId
                });

                setStatus('success');

            } else {
                setError(t('login.userNotFound', 'Kullanıcı bulunamadı. Lütfen kayıt olun.'));
                setStatus('idle');
            }
        } catch (err) {
            console.error(err);
            setError('Bir hata oluştu. Lütfen tekrar deneyin.');
            setStatus('idle');
        }
    };

    const sendOtpEmail = useCallback(async () => {
        const res = await fetch('/api/send-otp', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: formData.email, type: 'customer' })
        });
        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data.error || 'OTP gönderilemedi.');
        }
    }, [formData.email]);

    const handleRegister = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            const fullPhone = countryCode + formData.phone;
            const { data: existingRows, error: existErr } = await supabase
                .from('customers')
                .select('*')
                .eq('phone', fullPhone);
            if (existErr) throw existErr;

            if (existingRows && existingRows.length > 0) {
                setError('Bu telefon numarası ile kayıtlı bir kullanıcı zaten var. Lütfen giriş yapın.');
                setStatus('idle');
                return;
            }

            // Send OTP to email
            await sendOtpEmail();

            // Show OTP modal
            setOtpCode(['', '', '', '']);
            setOtpError('');
            setOtpResendCooldown(60);
            setShowOtpModal(true);
            setStatus('idle');

        } catch (err) {
            console.error(err);
            setError(err.message || 'Kayıt sırasında bir hata oluştu.');
            setStatus('idle');
        }
    };

    const handleOtpChange = (index, value) => {
        const digit = value.replace(/[^0-9]/g, '');
        if (digit.length > 1) return;

        const newOtp = [...otpCode];
        newOtp[index] = digit;
        setOtpCode(newOtp);
        setOtpError('');

        if (digit && index < 3) {
            otpInputRefs.current[index + 1]?.current?.focus();
        }

        if (digit && index === 3 && newOtp.every(d => d !== '')) {
            verifyOtp(newOtp.join(''));
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
            otpInputRefs.current[index - 1]?.current?.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 4);
        if (!pastedText) return;

        const newOtp = ['', '', '', ''];
        for (let i = 0; i < pastedText.length; i++) {
            newOtp[i] = pastedText[i];
        }
        setOtpCode(newOtp);
        setOtpError('');

        const focusIndex = Math.min(pastedText.length, 3);
        otpInputRefs.current[focusIndex]?.current?.focus();

        if (pastedText.length === 4) {
            verifyOtp(pastedText);
        }
    };

    const verifyOtp = async (code) => {
        setOtpLoading(true);
        setOtpError('');

        try {
            const targetEmail = checkInMode === 'forgot-password' ? resetEmail : formData.email;
            const res = await fetch('/api/verify-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: targetEmail, code })
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                if (data.error?.includes('expired') || data.error?.includes('suresi') || data.error?.includes('süresi')) {
                    setOtpError('Kodun süresi dolmuş. Lütfen yeni kod isteyin.');
                } else {
                    setOtpError('Girdiğiniz kod yanlış. Tekrar deneyin.');
                }
                setOtpCode(['', '', '', '']);
                setTimeout(() => {
                    otpInputRefs.current[0]?.current?.focus();
                }, 100);
                setOtpLoading(false);
                return;
            }

            setShowOtpModal(false);
            
            if (checkInMode === 'forgot-password') {
                setForgotPasswordStep(1);
                setStatus('idle');
            } else {
                await completeRegistration();
            }

        } catch (err) {
            console.error(err);
            setOtpError('Bir hata oluştu. Lütfen tekrar deneyin.');
            setOtpLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (otpResendCooldown > 0) return;
        setOtpError('');

        try {
            const targetEmail = checkInMode === 'forgot-password' ? resetEmail : formData.email;
            const res = await fetch('/api/send-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: targetEmail, type: 'customer' })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'OTP gönderilemedi.');
            }

            setOtpResendCooldown(60);
            setOtpCode(['', '', '', '']);
            setTimeout(() => {
                otpInputRefs.current[0]?.current?.focus();
            }, 100);
        } catch (err) {
            console.error(err);
            setOtpError(err.message || 'Kod gönderilemedi.');
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            const { data: rows, error: fetchErr } = await supabase.from('customers').select('*').eq('email', resetEmail);
            if (fetchErr) throw fetchErr;

            if (!rows || rows.length === 0) {
                setError('Bu e-posta adresi ile kayıtlı bir kullanıcı bulunamadı.');
                setStatus('idle');
                return;
            }

            const res = await fetch('/api/send-otp', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: resetEmail, type: 'customer' })
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || 'OTP gönderilemedi.');
            }

            setOtpCode(['', '', '', '']);
            setOtpError('');
            setOtpResendCooldown(60);
            setShowOtpModal(true);
            setStatus('idle');
        } catch (err) {
            console.error(err);
            setError(err.message || 'Bir hata oluştu.');
            setStatus('idle');
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            const { error: updateErr } = await supabase
                .from('customers')
                .update({ password: newPassword })
                .eq('email', resetEmail);

            if (updateErr) throw updateErr;

            setCheckInMode('login');
            setLoginIdentifier(resetEmail);
            setLoginPassword(newPassword);
            setForgotPasswordStep(0);
            setStatus('idle');
            setError(''); 
        } catch (err) {
            console.error(err);
            setError('Şifre sıfırlanırken bir hata oluştu.');
            setStatus('idle');
        }
    };

    const completeRegistration = async () => {
        setStatus('loading');
        setError('');

        try {
            let defaultTenantId = null;
            const { data: ugraTenants } = await supabase.from('tenants').select('*').eq('slug', 'ugra').limit(1);

            if (ugraTenants && ugraTenants.length > 0) {
                defaultTenantId = ugraTenants[0].id;
            } else {
                const { data: allTenants } = await supabase.from('tenants').select('*').limit(1);
                if (allTenants && allTenants.length > 0) defaultTenantId = allTenants[0].id;
            }

            if (!defaultTenantId) throw new Error("Sistemde kayıtlı işletme bulunamadı.");

            const { data: tData } = await supabase.from('tenants').select('*').eq('id', defaultTenantId).single();
            const rules = tData?.loyalty_rules || tData?.loyaltyRules || {};
            const welcomePoints = rules.welcomePoints || 0;

            const shortId = generateShortId();
            const { data: newCustomer, error: insertErr } = await supabase
                .from('customers')
                .insert({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    email: formData.email,
                    phone: countryCode + formData.phone,
                    tenant_id: defaultTenantId,
                    short_id: shortId,
                    points: welcomePoints,
                    total_points: welcomePoints,
                    point_earnings: welcomePoints > 0 ? [{ p: welcomePoints, t: new Date().toISOString() }] : [],
                    created_at: new Date().toISOString(),
                    visit_role: 'Yeni Üye',
                    password: formData.password
                })
                .select()
                .single();
            if (insertErr) throw insertErr;

            // Record KVKK/privacy consent (best-effort; won't break registration)
            await recordConsent(supabase, 'customers', newCustomer.id, new Date().toISOString());

            localStorage.setItem('crm_global_customer_id', newCustomer.id);
            const { data: mData } = await supabase.from('menus').select('*').eq('tenant_id', defaultTenantId).maybeSingle();

            setLoggedInTenant(tData);
            setMenuData(mData);
            setResult({
                isNew: true,
                name: formData.firstName,
                points: welcomePoints,
                total: welcomePoints,
                tenantId: defaultTenantId
            });

            setStatus('success');

        } catch (err) {
            console.error(err);
            setError(err.message || 'Kayıt sırasında bir hata oluştu.');
            setStatus('idle');
        }
    };

    if (status === 'success' && result) {
        return (
            <SuccessView
                result={result}
                tenant={loggedInTenant}
                menuData={menuData}
                isGlobal={true}
                onGoToPortal={() => navigate('/portal')}
                t={t}
            />
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: 'var(--bg-main)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                <div className="card glass" style={{ maxWidth: '450px', width: '100%', padding: '2.5rem', position: 'relative' }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{ position: 'absolute', left: '1.5rem', top: '1.5rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                    >
                        <ArrowLeft />
                    </button>
                    <div style={{ margin: '0 auto 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                        <Logo size={60} />
                        <span style={{ fontSize: '2.5rem', fontWeight: 600, fontFamily: 'var(--font-logo)', position: 'relative', top: '3px', letterSpacing: '-0.02em', color: 'var(--text-main)' }}>ugra</span>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', background: 'rgba(0,0,0,0.05)', padding: '4px', borderRadius: '12px' }}>
                        <button
                            onClick={() => { setCheckInMode('register'); setError(''); }}
                            style={{
                                flex: 1, padding: '10px', borderRadius: '10px',
                                background: checkInMode === 'register' ? 'white' : 'transparent',
                                color: checkInMode === 'register' ? 'var(--text-main)' : 'var(--text-muted)',
                                fontWeight: 700, fontSize: '0.875rem',
                                boxShadow: checkInMode === 'register' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            {t('checkin.modeRegister', 'Kayıt Ol')}
                        </button>
                        <button
                            onClick={() => { setCheckInMode('login'); setError(''); }}
                            style={{
                                flex: 1, padding: '10px', borderRadius: '10px',
                                background: checkInMode === 'login' ? 'white' : 'transparent',
                                color: checkInMode === 'login' ? 'var(--text-main)' : 'var(--text-muted)',
                                fontWeight: 700, fontSize: '0.875rem',
                                boxShadow: checkInMode === 'login' ? '0 4px 12px rgba(0,0,0,0.1)' : 'none',
                                border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            {t('checkin.modeLogin', 'Giriş Yap')}
                        </button>
                    </div>

                    <h1 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '0.5rem', textAlign: 'center' }}>
                        {checkInMode === 'register'
                            ? (formData.firstName ? `Merhaba, ${formData.firstName}!` : 'Aramıza Katılın')
                            : checkInMode === 'forgot-password' ? 'Şifrenizi Sıfırlayın' : 'Tekrar Hoş Geldiniz'
                        }
                    </h1>
                    <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginBottom: '2.5rem' }}>
                        {checkInMode === 'register' ? 'Fırsatlardan yararlanmak için hemen üye olun.' : checkInMode === 'forgot-password' ? 'E-posta adresinize bir doğrulama kodu göndereceğiz.' : 'Puanlarınızı görmek için giriş yapın.'}
                    </p>

                    {error && (
                        <div style={{ padding: '1rem', background: '#fee2e2', color: '#ef4444', borderRadius: '12px', marginBottom: '1.5rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <AlertCircle size={16} /> {error}
                        </div>
                    )}

                    {checkInMode === 'register' ? (
                        <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                        <User size={16} /> {t('checkin.firstName', 'Ad')}
                                    </label>
                                    <input
                                        required type="text" placeholder={t('common.firstName')}
                                        value={formData.firstName}
                                        onChange={e => setFormData({ ...formData, firstName: capitalizeWords(e.target.value) })}
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                        <User size={16} /> {t('checkin.lastName', 'Soyad')}
                                    </label>
                                    <input
                                        required type="text" placeholder={t('common.lastName')}
                                        value={formData.lastName}
                                        onChange={e => setFormData({ ...formData, lastName: capitalizeWords(e.target.value) })}
                                        style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Mail size={16} /> {t('checkin.email')}
                                </label>
                                <input
                                    required type="email" placeholder="ornek@email.com"
                                    value={formData.email}
                                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Phone size={16} /> {t('checkin.phone')}
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <select
                                        value={countryCode} onChange={e => setCountryCode(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600, width: '95px' }}
                                    >
                                        {countries.map((c, i) => (<option key={i} value={c.code}>{c.name} {c.code}</option>))}
                                    </select>
                                    <input
                                        required type="tel" placeholder="5XX XXX XX XX"
                                        value={formData.phone}
                                        onChange={e => {
                                            let val = e.target.value.replace(/[^0-9]/g, '');
                                            if (val.startsWith('0')) val = val.substring(1);
                                            if (val.length <= 10) setFormData({ ...formData, phone: val });
                                        }}
                                        style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                    />
                                </div>
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Lock size={16} /> {t('checkin.password', 'Şifre')}
                                </label>
                                <input
                                    required type="password" placeholder="••••••••"
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)' }}
                                />
                            </div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                                <input
                                    type="checkbox"
                                    checked={kvkkAccepted}
                                    onChange={e => setKvkkAccepted(e.target.checked)}
                                    style={{ width: '18px', height: '18px', marginTop: '1px', flexShrink: 0, cursor: 'pointer', accentColor: 'var(--primary)' }}
                                />
                                <span>
                                    <a href="/kvkk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>KVKK Aydınlatma Metni</a>
                                    {' '}ve{' '}
                                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', fontWeight: 700 }}>Gizlilik Sözleşmesi</a>
                                    {"'ni okudum, kişisel verilerimin işlenmesini kabul ediyorum."}
                                </span>
                            </label>
                            <button
                                type="submit" disabled={status === 'loading' || !kvkkAccepted}
                                style={{ marginTop: '0.5rem', padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, borderRadius: '16px', border: 'none', cursor: 'pointer', opacity: (status === 'loading' || !kvkkAccepted) ? 0.7 : 1 }}
                            >
                                {status === 'loading' ? t('common.loading') : 'Kayıt Ol'}
                            </button>
                        </form>
                    ) : checkInMode === 'forgot-password' ? (
                        <>
                            {forgotPasswordStep === 0 ? (
                                <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                            <Mail size={16} /> E-posta Adresi
                                        </label>
                                        <input
                                            required type="email" placeholder="Kayıtlı e-posta adresiniz"
                                            value={resetEmail}
                                            onChange={e => setResetEmail(e.target.value)}
                                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                        />
                                    </div>
                                    <button
                                        type="submit" disabled={status === 'loading'}
                                        style={{ marginTop: '1rem', padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, borderRadius: '16px', border: 'none', cursor: 'pointer', opacity: status === 'loading' ? 0.7 : 1 }}
                                    >
                                        {status === 'loading' ? t('common.loading') : 'Doğrulama Kodu Gönder'}
                                    </button>
                                    <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                        <button type="button" onClick={() => { setCheckInMode('login'); setError(''); setForgotPasswordStep(0); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
                                            Giriş ekranına dön
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    <div>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                            <Lock size={16} /> Yeni Şifre
                                        </label>
                                        <input
                                            required type="password" placeholder="••••••••"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                        />
                                    </div>
                                    <button
                                        type="submit" disabled={status === 'loading'}
                                        style={{ marginTop: '1rem', padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, borderRadius: '16px', border: 'none', cursor: 'pointer', opacity: status === 'loading' ? 0.7 : 1 }}
                                    >
                                        {status === 'loading' ? t('common.loading') : 'Şifreyi Kaydet'}
                                    </button>
                                </form>
                            )}
                        </>
                    ) : (
                        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Phone size={16} /> Telefon veya E-posta
                                </label>
                                <input
                                    required type="text" placeholder="5XX... veya e-posta"
                                    value={loginIdentifier}
                                    onChange={e => {
                                        let val = e.target.value;
                                        if (/^\d+$/.test(val) && val.startsWith('0')) val = val.substring(1);
                                        setLoginIdentifier(val);
                                    }}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600 }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 600 }}>
                                    <Lock size={16} /> Şifre
                                </label>
                                <input
                                    required type="password" placeholder="••••••••"
                                    value={loginPassword}
                                    onChange={e => setLoginPassword(e.target.value)}
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)' }}
                                />
                                <div style={{ textAlign: 'right', marginTop: '0.75rem' }}>
                                    <button type="button" onClick={() => { setCheckInMode('forgot-password'); setError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>
                                        Şifremi Unuttum
                                    </button>
                                </div>
                            </div>
                            <button
                                type="submit" disabled={status === 'loading'}
                                style={{ marginTop: '1rem', padding: '16px', background: 'var(--primary)', color: 'white', fontWeight: 700, borderRadius: '16px', border: 'none', cursor: 'pointer', opacity: status === 'loading' ? 0.7 : 1 }}
                            >
                                {status === 'loading' ? t('common.loading') : 'Giriş Yap'}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {showOtpModal && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
                    onClick={(e) => { if (e.target === e.currentTarget) setShowOtpModal(false); }}
                >
                    <div className="card glass" style={{ maxWidth: '400px', width: '100%', padding: '2.5rem', position: 'relative', textAlign: 'center', animation: 'fadeInUp 0.3s ease' }}>
                        <button onClick={() => setShowOtpModal(false)} style={{ position: 'absolute', right: '1rem', top: '1rem', background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                            <X size={20} />
                        </button>
                        <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(111, 78, 55, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                            <Mail size={28} color="#6f4e37" />
                        </div>
                        <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.5rem' }}>Doğrulama Kodu</h2>
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '2rem' }}>
                            <strong>{checkInMode === 'forgot-password' ? resetEmail : formData.email}</strong> adresine gönderilen 4 haneli kodu girin.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '1.5rem' }}>
                            {otpCode.map((digit, i) => (
                                <input
                                    key={i} ref={otpInputRefs.current[i]} type="text" inputMode="numeric" maxLength={1} value={digit}
                                    onChange={e => handleOtpChange(i, e.target.value)}
                                    onKeyDown={e => handleOtpKeyDown(i, e)}
                                    onPaste={i === 0 ? handleOtpPaste : undefined}
                                    disabled={otpLoading}
                                    style={{ width: '50px', height: '60px', textAlign: 'center', fontSize: '1.5rem', fontWeight: 700, borderRadius: '12px', border: `2px solid ${otpError ? '#ef4444' : digit ? '#6f4e37' : 'var(--border)'}`, background: 'var(--bg-main)', outline: 'none' }}
                                />
                            ))}
                        </div>
                        {otpError && <div style={{ padding: '0.75rem', background: '#fee2e2', color: '#ef4444', borderRadius: '10px', fontSize: '0.8125rem', marginBottom: '1rem' }}>{otpError}</div>}
                        <button
                            onClick={handleResendOtp} disabled={otpResendCooldown > 0 || otpLoading}
                            style={{ background: 'transparent', border: 'none', color: otpResendCooldown > 0 ? 'var(--text-muted)' : '#6f4e37', fontWeight: 600, fontSize: '0.875rem', cursor: otpResendCooldown > 0 ? 'default' : 'pointer', marginBottom: '1rem' }}
                        >
                            {otpResendCooldown > 0 ? `Tekrar gönder (${otpResendCooldown}s)` : 'Kodu tekrar gönder'}
                        </button>
                    </div>
                </div>
            )}
            <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        </div>
    );
};

export default CustomerLogin;
